/**
 * Create + Edit in one component (Products convention). Edit sends a diff
 * only: the backend 409s (`COUPON_ALREADY_USED`) on the mere *presence* of a
 * pricing field once a coupon has been redeemed, even when unchanged.
 */
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { ConfirmModal } from '@/src/modules/components/ui/ConfirmModal';
import { useCreateCoupon, useUpdateCoupon } from '../hooks';
import { errorCode } from '../services/api-client';
import {
  EMPTY_FORM,
  createCouponSchema,
  diffWire,
  editCouponSchema,
  toFormValues,
  toWire,
  type CouponFormValues,
} from '../schemas';
import { browserTimeZone, describeDiscount } from '../lib/discount';
import { CollectionScopePicker } from './CollectionScopePicker';
import type { CouponDetail } from '../types';

const inputClass =
  'w-full px-3 py-2 rounded-lg bg-surface-container-lowest border border-border-subtle text-sm focus:outline-none focus:ring-2 focus:ring-electric-violet/30 disabled:opacity-60';
const labelClass = 'block text-[10px] uppercase tracking-widest font-bold text-on-surface-variant mb-1';

type Props =
  | { open: boolean; mode: 'create'; onClose: () => void }
  | { open: boolean; mode: 'edit'; coupon: CouponDetail; onClose: () => void; onReload: () => void };

export function CouponForm(props: Props) {
  const { open, mode, onClose } = props;
  const coupon = mode === 'edit' ? props.coupon : undefined;
  const locked = !!coupon && coupon.usedCount > 0;
  const navigate = useNavigate();
  const createMutation = useCreateCoupon();
  const updateMutation = useUpdateCoupon(coupon?.id ?? '');
  const mutation = mode === 'create' ? createMutation : updateMutation;
  const [banner, setBanner] = useState<{ message: string; reload?: boolean } | null>(null);

  const form = useForm<CouponFormValues>({
    resolver: zodResolver(mode === 'create' ? createCouponSchema : editCouponSchema),
    defaultValues: coupon ? toFormValues(coupon) : EMPTY_FORM,
  });
  const { register, watch, setValue, setError, formState } = form;
  const errors = formState.errors;
  const codeField = register('code');

  // Re-seed whenever the modal opens or the coupon changes underneath (e.g. after "Reload latest").
  useEffect(() => {
    if (!open) return;
    form.reset(coupon ? toFormValues(coupon) : EMPTY_FORM);
    setBanner(null);
    mutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, coupon?.id, coupon?.version]);

  const values = watch();
  const isPct = values.discountType === 'PERCENTAGE';

  const submit = form.handleSubmit(async (v) => {
    setBanner(null);
    try {
      if (!coupon) {
        const created = await createMutation.mutateAsync({ code: v.code, ...toWire(v) });
        onClose();
        navigate(`/coupons/${created.id}`);
        return;
      }
      const diff = diffWire(toWire(toFormValues(coupon)), toWire(v));
      if (Object.keys(diff).length > 0) {
        await updateMutation.mutateAsync({ version: coupon.version, ...diff });
      }
      onClose();
    } catch (e) {
      const message = (e as Error).message;
      switch (errorCode(e)) {
        case 'COUPON_CODE_TAKEN':
          setError('code', { message: 'This code already exists.' });
          break;
        case 'COLLECTION_CODE_UNKNOWN':
          setError('collectionCodes', { message });
          break;
        case 'COUPON_VERSION_CONFLICT':
          setBanner({
            message: 'This coupon changed since you opened it (another admin or the expiry job). Reload to see the latest, then re-apply your edit.',
            reload: true,
          });
          break;
        case 'COUPON_ALREADY_USED':
          setBanner({
            message: 'Pricing fields are locked because this coupon has been redeemed. Reload to see the latest.',
            reload: true,
          });
          break;
        default:
          setBanner({ message });
      }
    }
  });

  const startInPast = mode === 'create' && values.startAt && new Date(values.startAt).getTime() < Date.now();
  const amountPreview = (() => {
    const n = Number(values.amount);
    if (!(n > 0) || (isPct && n > 100)) return null;
    const max = Number(values.maxDiscountRupees);
    return describeDiscount({
      discountType: values.discountType,
      discountValue: Math.round(n * 100),
      maxDiscountPaise: isPct && max > 0 ? Math.round(max * 100) : null,
    });
  })();

  return (
    <ConfirmModal
      open={open}
      title={coupon ? `Edit ${coupon.code}` : 'Create coupon'}
      confirmLabel={coupon ? 'Save changes' : 'Create as draft'}
      loading={mutation.isPending}
      onClose={onClose}
      onConfirm={() => void submit()}
    >
      <form onSubmit={(e) => void submit(e)} className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
        {mode === 'create' && (
          <div>
            <label htmlFor="coupon-code" className={labelClass}>
              Code (can't be changed later)
            </label>
            <input
              id="coupon-code"
              {...codeField}
              // Codes are case-sensitive uppercase (CD-2) — same as the customer CouponInput.
              onChange={(e) => {
                e.target.value = e.target.value.toUpperCase();
                void codeField.onChange(e);
              }}
              placeholder="FESTIVE100"
              className={`${inputClass} font-mono`}
            />
            {errors.code && <p className="text-xs text-red-300 mt-1">{errors.code.message}</p>}
          </div>
        )}
        <div>
          <label htmlFor="coupon-label" className={labelClass}>
            Label (shown to customers)
          </label>
          <input id="coupon-label" {...register('label')} placeholder="Festive season — flat ₹100 off" className={inputClass} />
          {errors.label && <p className="text-xs text-red-300 mt-1">{errors.label.message}</p>}
        </div>

        {locked && (
          <p className="text-xs rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-200 p-2">
            Redeemed {coupon!.usedCount} {coupon!.usedCount === 1 ? 'time' : 'times'} — discount, dates and limits are
            locked. Label and product scope can still change.
          </p>
        )}

        <fieldset disabled={locked} className="space-y-3">
          <div>
            <span className={labelClass}>Discount type</span>
            <div className="flex gap-4 text-sm">
              <label className="flex items-center gap-2">
                <input type="radio" value="FLAT" {...register('discountType')} /> Flat amount (₹)
              </label>
              <label className="flex items-center gap-2">
                <input type="radio" value="PERCENTAGE" {...register('discountType')} /> Percentage (%)
              </label>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="coupon-amount" className={labelClass}>
                {isPct ? 'Percent off' : 'Amount off (₹)'}
              </label>
              <input id="coupon-amount" type="number" step="0.01" min="0" {...register('amount')} className={inputClass} />
              {errors.amount && <p className="text-xs text-red-300 mt-1">{errors.amount.message}</p>}
            </div>
            {isPct && (
              <div>
                <label htmlFor="coupon-max" className={labelClass}>
                  Max discount (₹, optional)
                </label>
                <input id="coupon-max" type="number" step="0.01" min="0" {...register('maxDiscountRupees')} className={inputClass} />
                {errors.maxDiscountRupees && <p className="text-xs text-red-300 mt-1">{errors.maxDiscountRupees.message}</p>}
              </div>
            )}
          </div>
          {amountPreview && <p className="text-xs text-on-surface-variant">Customers get: {amountPreview}</p>}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="coupon-start" className={labelClass}>
                Starts
              </label>
              <input id="coupon-start" type="datetime-local" {...register('startAt')} className={inputClass} />
              {errors.startAt && <p className="text-xs text-red-300 mt-1">{errors.startAt.message}</p>}
            </div>
            <div>
              <label htmlFor="coupon-end" className={labelClass}>
                Ends
              </label>
              <input id="coupon-end" type="datetime-local" {...register('endAt')} className={inputClass} />
              {errors.endAt && <p className="text-xs text-red-300 mt-1">{errors.endAt.message}</p>}
            </div>
          </div>
          <p className="text-[10px] text-on-surface-variant">
            Times are in your timezone ({browserTimeZone()}).
            {startInPast && ' Start is in the past — the coupon is redeemable as soon as it is activated.'}
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="coupon-limit-global" className={labelClass}>
                Total uses (blank = unlimited)
              </label>
              <input id="coupon-limit-global" type="number" min="1" step="1" {...register('usageLimitGlobal')} className={inputClass} />
              {errors.usageLimitGlobal && <p className="text-xs text-red-300 mt-1">{errors.usageLimitGlobal.message}</p>}
            </div>
            <div>
              <label htmlFor="coupon-limit-user" className={labelClass}>
                Uses per customer (blank = unlimited)
              </label>
              <input id="coupon-limit-user" type="number" min="1" step="1" {...register('usageLimitPerUser')} className={inputClass} />
              {errors.usageLimitPerUser && <p className="text-xs text-red-300 mt-1">{errors.usageLimitPerUser.message}</p>}
            </div>
          </div>
        </fieldset>

        <div>
          <span className={labelClass}>Applies to</span>
          <CollectionScopePicker
            scope={values.scope}
            selected={values.collectionCodes}
            onScopeChange={(s) => setValue('scope', s, { shouldDirty: true, shouldValidate: formState.isSubmitted })}
            onSelectedChange={(codes) =>
              setValue('collectionCodes', codes, { shouldDirty: true, shouldValidate: formState.isSubmitted })
            }
          />
          {errors.collectionCodes && <p className="text-xs text-red-300 mt-1">{errors.collectionCodes.message}</p>}
          {locked && formState.dirtyFields.collectionCodes && (
            <p className="text-xs text-amber-200 mt-1">
              Narrowing scope can remove the discount for customers who already have this code in an open checkout.
            </p>
          )}
        </div>

        {banner && (
          <div className="text-xs rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 p-2 space-y-2">
            <p>{banner.message}</p>
            {banner.reload && mode === 'edit' && (
              <button type="button" onClick={props.onReload} className="underline font-bold">
                Reload latest
              </button>
            )}
          </div>
        )}
      </form>
    </ConfirmModal>
  );
}
